/**
 * Custom hook for fetching daily panchanga data
 */

import { useState, useEffect, useCallback } from 'react';
import { panchangaApi, ApiError } from '../services/api';
import type { PanchangaResponse, PanchangaRequestParams } from '../types/api';

interface UsePanchangaResult {
  data: PanchangaResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const API_TIMEOUT_MS = 15000;

export function usePanchanga(params: PanchangaRequestParams): UsePanchangaResult {
  const [data, setData] = useState<PanchangaResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const doFetch = async () => {
      setLoading(true);
      setError(null);

      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Request timed out. Please try again.')), API_TIMEOUT_MS);
        });
        const response = await Promise.race([
          panchangaApi.getDailyPanchanga(params),
          timeoutPromise,
        ]);
        if (!cancelled) setData(response);
      } catch (err) {
        if (!cancelled) {
          const errorMessage =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Failed to fetch panchanga data';
          setError(errorMessage);
          console.error('Error fetching panchanga:', err);
        }
      } finally {
        clearTimeout(timeoutId!);
        if (!cancelled) setLoading(false);
      }
    };

    doFetch();
    return () => { cancelled = true; clearTimeout(timeoutId!); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.date,
    params.location,
    params.latitude,
    params.longitude,
    params.altitude,
    params.locale,
    fetchTrigger,
  ]);

  const refetch = useCallback(() => setFetchTrigger(n => n + 1), []);

  return {
    data,
    loading,
    error,
    refetch,
  };
}
